import React from 'react'
import styled from 'styled-components'
import DataTable from 'react-data-table-component'
import { ArrowDown, Monitor, Smartphone } from 'react-feather'
import { Spinner, UncontrolledTooltip } from 'reactstrap'
import { useIntl, FormattedNumber, FormattedMessage } from 'react-intl'

import { Loading } from '../styles'

const customStyles = {
  headRow: {
    style: {
      backgroundColor: '#f8f8f8',
    },
  },
}

const locationCell = (row, index) => {
  return (
    <LocationDiv>
      <div>
        <span id={`location-name-cell-${index}`}>{row.location}</span>
        {row?.storecode && (
          <span
            className="storecode-tag"
            id={`location-storecode-cell-${index}`}
          >
            {row.storecode}
          </span>
        )}
      </div>
      <div className="text-muted" id={`location-address-cell-${index}`}>
        {row.address} - {row.city}
      </div>
      <UncontrolledTooltip target={`location-name-cell-${index}`}>
        {row.location}
      </UncontrolledTooltip>
      <UncontrolledTooltip target={`location-address-cell-${index}`}>
        {row.address} - {row.city}
      </UncontrolledTooltip>
      {row?.storecode && (
        <UncontrolledTooltip target={`location-storecode-cell-${index}`}>
          {row.storecode}
        </UncontrolledTooltip>
      )}
    </LocationDiv>
  )
}

const googleSearchCell = (row, index) => {
  const isTotalZero = +row.googlesearchtotal === 0
  const total = +row.googlesearchtotal
  const desktop = +row.googlesearchdesktop
  const mobile = +row.googlesearchmobile
  const desktopPercentage = Math.round((desktop / total) * 100)
  const mobilePercentage = Math.round((mobile / total) * 100)

  return (
    <GoogleSearchDiv>
      <div className="total-search">
        <FormattedNumber value={total} />
      </div>
      <div className="meters-search">
        {isTotalZero && (
          <div className="percentage-total">
            <div className="no-percentage w-100"></div>
          </div>
        )}
        {!isTotalZero && (
          <>
            <div className="percent-legend">{desktopPercentage}%</div>
            <div className="percentage-total">
              <div
                id={`search-desktop-percentage-cell-${index}`}
                className="desktop-percentage"
                style={{ width: `${desktopPercentage - 1}%` }}
              ></div>
              <div
                id={`search-mobile-percentage-cell-${index}`}
                className="mobile-percentage"
                style={{ width: `${mobilePercentage - 1}%` }}
              ></div>
              <UncontrolledTooltip
                target={`search-desktop-percentage-cell-${index}`}
              >
                <FormattedNumber value={desktop} />
              </UncontrolledTooltip>
              <UncontrolledTooltip
                target={`search-mobile-percentage-cell-${index}`}
              >
                <FormattedNumber value={mobile} />
              </UncontrolledTooltip>
            </div>
            <div className="percent-legend">{mobilePercentage}%</div>
          </>
        )}
      </div>
    </GoogleSearchDiv>
  )
}

const googleMapsCell = (row, index) => {
  const isTotalZero = +row.googlesearchtotal === 0
  const total = +row.googlemapstotal
  const desktop = +row.googlemapsdesktop
  const mobile = +row.googlemapsmobile
  const desktopPercentage = Math.round((desktop / total) * 100)
  const mobilePercentage = Math.round((mobile / total) * 100)

  return (
    <GoogleSearchDiv>
      <div className="total-search">
        <FormattedNumber value={total} />
      </div>
      <div className="meters-search">
        {isTotalZero && (
          <div className="percentage-total">
            <div className="no-percentage w-100"></div>
          </div>
        )}
        {!isTotalZero && (
          <>
            <div className="percent-legend">{desktopPercentage}%</div>
            <div className="percentage-total">
              <div
                id={`maps-desktop-percentage-cell-${index}`}
                className="desktop-percentage"
                style={{ width: `${desktopPercentage - 1}%` }}
              ></div>
              <div
                id={`maps-mobile-percentage-cell-${index}`}
                className="mobile-percentage"
                style={{ width: `${mobilePercentage - 1}%` }}
              ></div>
              <UncontrolledTooltip
                target={`maps-desktop-percentage-cell-${index}`}
              >
                <FormattedNumber value={desktop} />
              </UncontrolledTooltip>
              <UncontrolledTooltip
                target={`maps-mobile-percentage-cell-${index}`}
              >
                <FormattedNumber value={mobile} />
              </UncontrolledTooltip>
            </div>
            <div className="percent-legend">{mobilePercentage}%</div>
          </>
        )}
      </div>
    </GoogleSearchDiv>
  )
}

const columns = [
  {
    id: 'location',
    name: <FormattedMessage id="Location" />,
    width: '33%',
    selector: row => row.location,
    cell: locationCell,
    sortable: true,
  },
  {
    id: 'googleSearch',
    name: <FormattedMessage id="Google Search" />,
    width: '33%',
    minWidth: '270px',
    selector: row => +row.googlesearchtotal,
    cell: googleSearchCell,
    center: true,
    sortable: true,
  },
  {
    id: 'googleMaps',
    name: <FormattedMessage id="Google Maps" />,
    width: '33%',
    minWidth: '270px',
    selector: row => +row.googlemapstotal,
    cell: googleMapsCell,
    center: true,
    sortable: true,
  },
]

const LoadingData = () => {
  return (
    <Loading>
      <Spinner color="primary" size="lg" />
    </Loading>
  )
}

export const BehaviorPage = ({
  loading,
  data,
  total,
  setPageParams,
  setLimitParams,
  setSortParams,
}) => {
  const intl = useIntl()

  const paginationComponentOptions = {
    rowsPerPageText: intl.formatMessage({ id: 'table.pagination' }),
    rangeSeparatorText: intl.formatMessage({ id: 'table.rangeSeparator' }),
    selectAllRowsItem: false,
  }

  return (
    <ReputationDiv>
      <div className="header">
        {/* <h4>{intl.formatMessage({ id: 'Behavior' })}</h4> */}
        <div className="legend">
          <div className="blue">
            <Monitor size="15" color="#2B6CB0" />
            <span>Desktop</span>
          </div>

          <div className="orange">
            <Smartphone size="15" color="#C05621" />
            <span>Smartphone</span>
          </div>
        </div>
      </div>

      <DataTable
        actions
        keyField="behavior"
        className="dataTable-custom"
        columns={columns}
        data={data}
        noHeader
        pagination
        paginationServer
        paginationTotalRows={total}
        sortIcon={<ArrowDown size="4" />}
        onChangePage={setPageParams}
        onChangeRowsPerPage={setLimitParams}
        sortServer
        onSort={setSortParams}
        progressPending={loading}
        progressComponent={<LoadingData />}
        customStyles={customStyles}
        paginationComponentOptions={paginationComponentOptions}
      />
    </ReputationDiv>
  )
}

const ReputationDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin: 2em 0;

  .header {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1em;
  }

  .legend {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1em;

    > div {
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 0.5rem;
      gap: 0.5em;
      font-weight: 500;
      font-size: 0.9em;
      padding: 0.2em 0.5em;
    }

    > div.blue {
      background-color: #e2f4ff;
      color: #2b6cb0;
    }

    > div.orange {
      background-color: #ffead3;
      color: #c05621;
    }
  }

  h4 {
    font-size: 1.5em;
    color: #767676;
  }
`

const GoogleSearchDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  width: 100%;

  .total-search {
    font-size: 1.2em;
  }

  .percent-legend {
    color: #b8c2cc;
    white-space: nowrap;
  }

  .meters-search {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .desktop-percentage {
    height: 0.6em;
    min-height: 0.6em;
    background-color: #e2f4ff;
    border-radius: 99999px;
    transition: height 0.1s ease;

    &:hover {
      height: 0.8em;
    }
  }

  .mobile-percentage {
    height: 0.6em;
    min-height: 0.6em;
    background-color: #ffead3;
    border-radius: 99999px;
    transition: height 0.1s;

    &:hover {
      height: 0.8em;
    }
  }

  .no-percentage {
    height: 0.6em;
    min-height: 0.6em;
    background-color: #e8e8e8;
    border-radius: 99999px;
    transition: height 0.1s;

    &:hover {
      height: 0.8em;
    }
  }

  .percentage-total {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 80%;
  }
`

const LocationDiv = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;

  .storecode-tag {
    background-color: #f7fafc;
    color: #4a5568;
    border-radius: 4px;
    padding: 0.1rem 0.5rem;
    font-family: 'Inter', sans-serif;
    font-size: 0.9rem;
    font-weight: 500;
  }

  > div {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;

    span {
      margin-right: 0.5em;
    }
  }
`
